import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Box, Card, IconButton, InputAdornment, TextField, Tooltip, useMediaQuery } from '@mui/material'
import { Column, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { IconSearch, IconArrowRight } from '@tabler/icons-react'

import { TableApiV2, TableHeaderApi, ContentTitle, FilterButton, ContractStatus } from '~/components'
import { FilterContractDrawer } from './components'

import { SearchQueysData, useDebounce, usePermission, useQueryString, useToggle } from '~/hooks'
import { displayCpfCnpj, ifIsDocumentReturnFormated, theme } from '~/utils'
import { ContractModel, EContractStatus, useContractListQuery } from '~/graphql/types'

const queryKeys: SearchQueysData[] = [
  {
    key: 'pendingDocs', defaultValue: '', parseValue: true
  },
  {
    key: 'inPendenciesStatus', defaultValue: '', parseValue: true
  },
  { key: 'name', defaultValue: '' },
  { key: 'begin', defaultValue: '' },
  { key: 'end', defaultValue: '' },
]

export const ContractListAnalysis: React.FC = () => {
  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle(false)
  const { searchQuerys, paginationQuerySize, searchParams, setSearchParams, page, size } = useQueryString()

  const queryValues = searchQuerys(queryKeys)

  const canEditProposals = usePermission(['proposals.create', 'proposals.updated'])
  const isLowerlg = useMediaQuery(theme.breakpoints.down('xl'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [filterName, setFilterName] = useState(queryValues.name)

  const debouncedFilterName = useDebounce(filterName, 1000)

  useEffect(() => {
    setSearchParams(state => {
      if (debouncedFilterName) {
        state.set('name', debouncedFilterName)
        state.set('page', '0')
      } else {
        state.delete('name')
      }
      return state
    })
  }, [debouncedFilterName])

  const { data, loading } = useContractListQuery({
    variables: {
      params: {
        inStatus: EContractStatus.reserved,
        isFormalizedByPartner: true,
        pageSize: size,
        pageNumber: page,
        sort: {
          field: 'createdAt',
          order: -1
        },
        ...queryValues?.name?.length > 0 ? { nameOrDocument: ifIsDocumentReturnFormated(queryValues.name) } : {},
        ...queryValues?.status?.length > 0 ? { inStatus: queryValues.status } : {},
        // ...queryValues?.pendencies?.length > 0 ? { inPendencies: queryValues.pendencies } : {},
        ...queryValues?.pendingDocs?.length > 0 ? { inPendenciesStatus: queryValues.pendingDocs } : {},
        ...queryValues?.inPendenciesStatus?.length > 0 ? { inPendenciesStatus: queryValues.inPendenciesStatus } : {},
        ...queryValues?.begin && queryValues?.end ?
          {
            dateRange:
            {
              startDate: new Date(queryValues.begin),
              endDate: new Date(`${queryValues.end}Z23:59:59`)
            }
          }
          : {},
      }
    },
    onCompleted(result) {
      setSearchParams(state => {
        state.set('items', String(result.contractList.meta.documentCounts))
        return state
      })
    },
    fetchPolicy: 'no-cache'
  })

  const linkToContract = queryValues.inPendenciesStatus.length > 0

  const columns = useMemo((): Column[] => {
    return [
      {
        Header: 'Cliente',
        accessor: 'customerRef.name',
      },
      {
        Header: 'Documento',
        accessor: 'customerRef.document',
        Cell: (props) => <>{displayCpfCnpj(props.value)}</>,
        disableSortBy: true,
        filter: 'includes'
      },
      {
        Header: 'Telefone',
        accessor: 'customerRef.phone',
      },
      {
        Header: 'E-mail',
        accessor: 'customerRef.email',
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: (props) => <ContractStatus status={props.value} />
      },
      {
        Header: 'Ações',
        accessor: '_id',
        disableSortBy: true,
        Cell: ({ value, row }) => (

          <>
            {canEditProposals && (row?.original as ContractModel)?.isFormalizedByPartner !== false ? (
              <Link to={linkToContract ? `/app/contracts/${value}` : `/app/contracts/${value}/pendencies`}>
                <IconButton>
                  <IconArrowRight />
                </IconButton>
              </Link>
            ) : (
              <Tooltip title='Essa proposta não foi formalizada pela área do parceiro'>
                <span>
                  <IconButton disabled={true}>
                    <IconArrowRight />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        ),
      },
    ]
  }, [linkToContract])

  const dataWithMemo = useMemo(() => data?.contractList?.data || [], [data])

  const defaultColumnHiddens = isLowerlg ? ['createdAt'].concat(isLowerMd ? [] : '').concat(isLowerSm ? ['customerRef.document'] : '') : []

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    defaultColumn: {
      Filter: <></>
    },
    manualPagination: true,
    initialState: {
      hiddenColumns: [...defaultColumnHiddens, 'customerRef.personType']
    },
  },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  )

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title='Analisar documentos'
          description='Analisar documentos de contratos'
          breadcrumbLinks={{ currentLink: 'Contratos', links: [{ href: '/app/contracts', label: 'Contratos' }] }}
        />
        <TableHeaderApi
          startElement={
            (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%', flexFlow: isLowerSm ? 'column-reverse' : 'row' }}>
                <TextField
                  fullWidth
                  sx={{ maxWidth: '400px' }}
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder='Buscar cliente'
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position='end'>
                          <IconSearch />
                        </InputAdornment>
                      </>
                    )
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <FilterButton onCustomClear={() => setFilterName('')} filtersToClear={queryKeys} toggleDrawer={toggleDrawer} filterCounter={searchParams.size - paginationQuerySize} />
                </Box>

                <FilterContractDrawer queryKeys={queryKeys} drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} toggleDrawer={toggleDrawer} />
              </Box>
            )
          }
        />
        <TableApiV2
          isLoading={loading}
          tableMethods={tableMethods}
          columns={columns}
        />
      </Card>
    </div>
  )
}
